import axios from 'axios';
import { API_CONFIG, getDefaultHeaders } from '../config/api';
import authService from './authService';

class DataService {
  constructor() {
    this.apiClient = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      headers: getDefaultHeaders(),
      timeout: 10000 // 10 segundos de timeout
    });

    // Interceptor para adicionar token automaticamente
    this.apiClient.interceptors.request.use(
      (config) => {
        // Sempre usa o Bearer token da configuração
        config.headers.Authorization = `Bearer ${API_CONFIG.API_KEY}`;
        
        console.log('🔐 Headers da requisição:', {
          Authorization: `Bearer ${API_CONFIG.API_KEY.substring(0, 10)}...`,
          'Content-Type': config.headers['Content-Type']
        });
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cache para otimizar buscas
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutos
  }

  /**
   * Verifica se o cache é válido
   * @param {string} key - Chave do cache
   * @returns {boolean} - Se o cache é válido
   */
  isCacheValid(key) {
    const expiry = this.cacheExpiry.get(key);
    return expiry && Date.now() < expiry;
  }

  /**
   * Define um item no cache
   * @param {string} key - Chave do cache
   * @param {any} data - Dados a serem armazenados
   */
  setCache(key, data) {
    this.cache.set(key, data);
    this.cacheExpiry.set(key, Date.now() + this.cacheTimeout);
  }

  /**
   * Obtém um item do cache
   * @param {string} key - Chave do cache
   * @returns {any} - Dados do cache
   */
  getCache(key) {
    return this.cache.get(key);
  }

  /**
   * Autentica o usuário e obtém token JWT
   * @param {Object} credentials - Credenciais de login
   * @returns {Promise<Object>} - Resultado da autenticação
   */
  async authenticate(credentials = {}) {
    try {
      console.log('🔐 Tentando autenticar com:', {
        login: credentials.login,
        endpoint: API_CONFIG.ENDPOINTS.AUTH,
        baseURL: this.apiClient.defaults.baseURL
      });
      
      const response = await this.apiClient.post(API_CONFIG.ENDPOINTS.AUTH, credentials);
      
      if (response.data && response.data.token) {
        authService.setToken(response.data.token);
        console.log('✅ Autenticação bem-sucedida');
        return {
          success: true,
          data: response.data,
          message: 'Autenticação realizada com sucesso'
        };
      } else {
        console.log('❌ Resposta de autenticação inválida:', response.data);
        return {
          success: false,
          data: null,
          message: 'Resposta de autenticação inválida'
        };
      }
    } catch (error) {
      // Verifica se é erro de CORS, rede ou 404
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message.includes('Network Error') ||
                         error.message.includes('CORS');
      const is404Error = error.response?.status === 404 || 
                        error.message.includes('404') ||
                        error.message.includes('Not Found');
      
      if (isCorsError || is404Error) {
        console.log('API indisponível (CORS/Network/404) - autenticação não disponível');
      } else {
        console.error('Erro na autenticação:', error);
      }
      
      return {
        success: false,
        data: null,
        message: 'API indisponível'
      };
    }
  }

  /**
   * Busca lista de cidades (dados locais apenas)
   * @param {Object} params - Parâmetros de busca
   * @param {string} params.search - Termo de busca
   * @param {number} params.limit - Limite de resultados
   * @returns {Promise<Object>} - Lista de cidades
   */
  async getCities(params = {}) {
    try {
      const { search = '', limit = 50 } = params;
      const cacheKey = `cities_${search}_${limit}`;

      // Verifica cache primeiro
      if (this.isCacheValid(cacheKey)) {
        return {
          success: true,
          data: this.getCache(cacheKey),
          message: 'Cidades carregadas do cache',
          fromCache: true
        };
      }

      // Retorna apenas Apucarana, PR como cidade disponível
      const cities = this.getLocalCities(search);
      this.setCache(cacheKey, cities);

      return {
        success: true,
        data: cities,
        message: 'Cidades carregadas com sucesso',
        fromCache: false
      };
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
      
      // Se falhar, retorna cidades padrão como fallback
      const fallbackCities = this.getLocalCities(params.search);
      
      return {
        success: true,
        data: fallbackCities,
        message: 'Usando dados locais',
        fromCache: false,
        fallback: true
      };
    }
  }

  /**
   * Cidades locais disponíveis
   * @param {string} search - Termo de busca
   * @returns {Array} - Lista de cidades
   */
  getLocalCities(search = '') {
    const cities = [
      { 
        id: 1, 
        name: 'Apucarana', 
        state: 'PR', 
        fullName: 'Apucarana, PR',
        coordinates: {
          lat: -23.5505,
          lng: -51.4614
        }
      }
    ];

    if (!search) return cities;

    return cities.filter(city =>
      city.fullName.toLowerCase().includes(search.toLowerCase()) ||
      city.name.toLowerCase().includes(search.toLowerCase())
    );
  }

  /**
   * Veículos de exemplo como fallback
   * @param {string} search - Termo de busca
   * @returns {Array} - Lista de veículos
   */
  getFallbackVehicles(search = '') {
    const vehicles = [
      { id: 1, brand: 'Volkswagen', model: 'FOX', year: 2010, category: 'popular' },
      { id: 2, brand: 'Chevrolet', model: 'CORSA', year: 2008, category: 'popular' },
      { id: 3, brand: 'Honda', model: 'FIT', year: 2012, category: 'popular' },
      { id: 4, brand: 'Renault', model: 'SANDERO', year: 2015, category: 'popular' },
      { id: 5, brand: 'Honda', model: 'HR-V', year: 2020, category: 'popular' },
      { id: 6, brand: 'Chevrolet', model: 'ONIX', year: 2018, category: 'popular' },
      { id: 7, brand: 'Citroën', model: 'C3', year: 2016, category: 'popular' },
      { id: 8, brand: 'Chevrolet', model: 'CELTA', year: 2010, category: 'popular' }
    ];

    if (!search) return vehicles;

    return vehicles.filter(vehicle =>
      vehicle.brand.toLowerCase().includes(search.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(search.toLowerCase())
    );
  }

  /**
   * Marcas e modelos de exemplo como fallback
   * @param {string} search - Termo de busca
   * @returns {Array} - Lista de marcas com modelos
   */
  getFallbackBrandsAndModels(search = '') {
    const brandsAndModels = [
      {
        id: 'volkswagen',
        name: 'Volkswagen',
        logo: null,
        models: [
          { id: 'volkswagen-fox', name: 'FOX', yearStart: 2003, yearEnd: 2014, category: 'popular' }
        ]
      },
      {
        id: 'chevrolet',
        name: 'Chevrolet',
        logo: null,
        models: [
          { id: 'chevrolet-corsa', name: 'CORSA', yearStart: 2000, yearEnd: 2014, category: 'popular' },
          { id: 'chevrolet-onix', name: 'ONIX MANUAL', yearStart: 2012, yearEnd: 2024, category: 'popular' },
          { id: 'chevrolet-celta', name: 'CELTA', yearStart: 2006, yearEnd: 2016, category: 'popular' }
        ]
      },
      {
        id: 'honda',
        name: 'Honda',
        logo: null,
        models: [
          { id: 'honda-fit', name: 'FIT', yearStart: 2003, yearEnd: 2014, category: 'popular' },
          { id: 'honda-hrv', name: 'HR-V', yearStart: 2015, yearEnd: 2025, category: 'popular' }
        ]
      },
      {
        id: 'renault',
        name: 'Renault',
        logo: null,
        models: [
          { id: 'renault-sandero', name: 'SANDERO', yearStart: 2007, yearEnd: 2016, category: 'popular' }
        ]
      },
      {
        id: 'citroen',
        name: 'Citroën',
        logo: null,
        models: [
          { id: 'citroen-c3', name: 'C3', yearStart: 2003, yearEnd: 2023, category: 'popular' }
        ]
      }
    ];

    if (!search) return brandsAndModels;

    return brandsAndModels.filter(brand =>
      brand.name.toLowerCase().includes(search.toLowerCase()) ||
      brand.models.some(model => 
        model.name.toLowerCase().includes(search.toLowerCase())
      )
    ).map(brand => ({
      ...brand,
      models: brand.models.filter(model =>
        model.name.toLowerCase().includes(search.toLowerCase())
      )
    }));
  }

  /**
   * Busca lista de veículos
   * @param {Object} params - Parâmetros de busca
   * @returns {Promise<Object>} - Lista de veículos
   */
  async getVehicles(params = {}) {
    try {
      const { search = '', limit = 50, page = 1 } = params;
      const cacheKey = `vehicles_${search}_${limit}_${page}`;

      if (this.isCacheValid(cacheKey)) {
        return {
          success: true,
          data: this.getCache(cacheKey),
          message: 'Veículos carregados do cache',
          fromCache: true
        };
      }

      const queryParams = new URLSearchParams({
        ...(search && { search }),
        limit: limit.toString(),
        page: page.toString()
      });

      const response = await this.apiClient.get(
        `${API_CONFIG.ENDPOINTS.VEHICLES}?${queryParams}`
      );

      const vehicles = this.formatVehiclesData(response.data);
      this.setCache(cacheKey, vehicles);

      return {
        success: true,
        data: vehicles,
        message: 'Veículos carregados com sucesso',
        fromCache: false
      };
    } catch (error) {
      // Verifica se é erro de CORS, rede ou 404
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message.includes('Network Error') ||
                         error.message.includes('CORS');
      const is404Error = error.response?.status === 404 || 
                        error.message.includes('404') ||
                        error.message.includes('Not Found');
      
      if (isCorsError) {
        console.log('API indisponível (CORS/Network) - usando dados locais');
      } else if (is404Error) {
        console.log('Endpoint não encontrado (404) - usando dados locais');
      } else {
        console.error('Erro ao buscar veículos:', error);
      }
      
      // Retorna dados de exemplo se a API falhar
      const fallbackVehicles = this.getFallbackVehicles(params.search);
      
      return {
        success: true,
        data: fallbackVehicles,
        message: 'Usando dados locais - API indisponível',
        fromCache: false,
        fallback: true
      };
    }
  }

  /**
   * Busca marcas e modelos de veículos agrupados
   * @param {Object} params - Parâmetros de busca
   * @returns {Promise<Object>} - Lista de marcas com modelos
   */
  async getVehicleBrandsAndModels(params = {}) {
    try {
      const { search = '', limit = 100 } = params;
      const cacheKey = `vehicle_brands_models_${search}_${limit}`;

      if (this.isCacheValid(cacheKey)) {
        return {
          success: true,
          data: this.getCache(cacheKey),
          message: 'Dados carregados do cache',
          fromCache: true
        };
      }

      const queryParams = new URLSearchParams({
        ...(search && { search }),
        limit: limit.toString()
      });

      const fullUrl = `${API_CONFIG.ENDPOINTS.VEHICLES}?${queryParams}`;
        
      console.log('🚗 Buscando veículos via Proxy Reverso:', {
        url: fullUrl,
        method: 'GET',
        environment: process.env.NODE_ENV,
        proxy: 'setupProxy.js',
        headers: {
          'Authorization': `Bearer ${API_CONFIG.API_KEY.substring(0, 10)}...`,
          'Content-Type': 'application/json'
        }
      });
      
      const response = await this.apiClient.get(
        `${API_CONFIG.ENDPOINTS.VEHICLES}?${queryParams}`
      );

      console.log('✅ Resposta da API:', response.data);
      
      const brandsAndModels = this.formatBrandsAndModelsData(response.data);
      this.setCache(cacheKey, brandsAndModels);

      return {
        success: true,
        data: brandsAndModels,
        message: 'Veículos carregados com sucesso da API',
        fromCache: false
      };
    } catch (error) {
      console.error('❌ Erro ao buscar veículos:', error);
      
      // Retorna dados de exemplo se a API falhar
      const fallbackData = this.getFallbackBrandsAndModels(params.search);
      
      return {
        success: true,
        data: fallbackData,
        message: 'Usando dados locais - API indisponível',
        fromCache: false,
        fallback: true
      };
    }
  }

  /**
   * Busca um veículo específico por ID
   * @param {string} id - ID do veículo
   * @returns {Promise<Object>} - Dados do veículo
   */
  async getVehicleById(id) {
    try {
      const cacheKey = `vehicle_${id}`;

      if (this.isCacheValid(cacheKey)) {
        return {
          success: true,
          data: this.getCache(cacheKey),
          message: 'Veículo carregado do cache',
          fromCache: true
        };
      }

      const response = await this.apiClient.get(
        API_CONFIG.ENDPOINTS.VEHICLE_BY_ID(id)
      );

      const vehicle = this.formatVehicleData(response.data);
      this.setCache(cacheKey, vehicle);

      return {
        success: true,
        data: vehicle,
        message: 'Veículo carregado com sucesso',
        fromCache: false
      };
    } catch (error) {
      console.error('Erro ao buscar veículo:', error);
      
      return {
        success: false,
        data: null,
        message: 'Erro ao buscar veículo',
        fromCache: false
      };
    }
  }

  /**
   * Busca produtos
   * @param {Object} params - Parâmetros de busca
   * @returns {Promise<Object>} - Lista de produtos
   */
  async getProducts(params = {}) {
    try {
      const { search = '', limit = 50, page = 1 } = params;
      const cacheKey = `products_${search}_${limit}_${page}`;

      if (this.isCacheValid(cacheKey)) {
        return {
          success: true,
          data: this.getCache(cacheKey),
          message: 'Produtos carregados do cache',
          fromCache: true
        };
      }

      const queryParams = new URLSearchParams({
        ...(search && { search }),
        limit: limit.toString(),
        page: page.toString()
      });

      const response = await this.apiClient.get(
        `${API_CONFIG.ENDPOINTS.PRODUCTS}?${queryParams}`
      );

      const products = this.formatProductsData(response.data);
      this.setCache(cacheKey, products);

      return {
        success: true,
        data: products,
        message: 'Produtos carregados com sucesso',
        fromCache: false
      };
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      
      return {
        success: false,
        data: [],
        message: 'Erro ao buscar produtos',
        fromCache: false
      };
    }
  }

  /**
   * Formata dados de veículos
   * @param {Array|Object} data - Dados brutos
   * @returns {Array} - Veículos formatados
   */
  formatVehiclesData(data) {
    const vehicles = Array.isArray(data) ? data : (data.data || data.veiculos || []);
    
    return vehicles.map(vehicle => ({
      id: vehicle.id || vehicle.codigo,
      brand: vehicle.marca || vehicle.brand,
      model: vehicle.modelo || vehicle.model,
      year: vehicle.ano || vehicle.year,
      category: vehicle.categoria || 'popular',
      specifications: {
        engine: vehicle.motor || vehicle.engine,
        fuel: vehicle.combustivel || vehicle.fuel,
        transmission: vehicle.transmissao || vehicle.transmission
      }
    }));
  }

  /**
   * Formata dados de marcas e modelos agrupados
   * @param {Array|Object} data - Dados brutos
   * @returns {Array} - Marcas com modelos formatados
   */
  formatBrandsAndModelsData(data) {
    console.log('📊 Formatando dados recebidos:', data);
    
    const vehicles = Array.isArray(data) ? data : (data.data || data.veiculos || []);
    console.log('🚗 Veículos encontrados:', vehicles.length);
    
    // Agrupa veículos por marca
    const brandsMap = new Map();
    
    vehicles.forEach(vehicle => {
      // Extrai marca e modelo do campo 'veiculo' (ex: "FORD KA+ Todos os modelos 2015-2018")
      const vehicleName = vehicle.veiculo || '';
      const brandMatch = vehicleName.match(/^([A-Z]+)/);
      const modelMatch = vehicleName.match(/^[A-Z]+\s+([A-Z0-9+\-]+)/);
      
      const brandName = brandMatch ? brandMatch[1] : 'Outros';
      const modelName = modelMatch ? modelMatch[1] : vehicleName;
      const yearStart = vehicle.ano_de || 2000;
      const yearEnd = vehicle.ano_ate || 2025;
      
      if (!brandsMap.has(brandName)) {
        brandsMap.set(brandName, {
          id: brandName.toLowerCase(),
          name: brandName,
          logo: null,
          models: []
        });
      }
      
      const brand = brandsMap.get(brandName);
      const existingModel = brand.models.find(m => m.name === modelName);
      
      if (!existingModel) {
        brand.models.push({
          id: `${brandName}-${modelName}`.toLowerCase(),
          name: modelName,
          yearStart: parseInt(yearStart),
          yearEnd: parseInt(yearEnd),
          category: 'popular'
        });
      }
    });
    
    const result = Array.from(brandsMap.values());
    console.log('🏷️ Marcas formatadas:', result.length);
    return result;
  }

  /**
   * Formata dados de um veículo específico
   * @param {Object} data - Dados brutos
   * @returns {Object} - Veículo formatado
   */
  formatVehicleData(data) {
    const vehicle = data.data || data;
    
    return {
      id: vehicle.id || vehicle.codigo,
      brand: vehicle.marca || vehicle.brand,
      model: vehicle.modelo || vehicle.model,
      year: vehicle.ano || vehicle.year,
      category: vehicle.categoria || 'popular',
      specifications: {
        engine: vehicle.motor || vehicle.engine,
        fuel: vehicle.combustivel || vehicle.fuel,
        transmission: vehicle.transmissao || vehicle.transmission
      }
    };
  }

  /**
   * Formata dados de produtos
   * @param {Array|Object} data - Dados brutos
   * @returns {Array} - Produtos formatados
   */
  formatProductsData(data) {
    const products = Array.isArray(data) ? data : (data.data || data.produtos || []);
    
    return products.map(product => ({
      id: product.id || product.codigo,
      name: product.nome || product.produto,
      category: product.categoria || 'bateria',
      price: {
        min: product.preco_min || product.preco || 0,
        max: product.preco_max || product.preco || 0,
        currency: 'BRL'
      },
      specifications: {
        voltage: product.voltagem || '12V',
        amperage: product.amperagem || 'N/A',
        type: product.tipo || 'Chumbo-ácido',
        warranty: product.garantia || '12 meses'
      },
      availability: product.disponivel || true,
      image: product.imagem || null
    }));
  }

  /**
   * Limpa todo o cache
   */
  clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  /**
   * Obtém estatísticas do cache
   * @returns {Object} - Estatísticas
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      validKeys: Array.from(this.cache.keys()).filter(key => this.isCacheValid(key))
    };
  }
}

// Exporta uma instância singleton
export default new DataService();
